// ============================================================================
// Asset location — the read side (where is it, where has it been, where can it
// go) and the write side (four RPCs, no direct table writes).
//
// Every mutation here goes through a SECURITY DEFINER RPC. That is not
// ceremony: the RPC writes the floor_machines projection AND the
// asset_movement_events row in one transaction, so the current location and
// its history cannot drift apart. It also decides WHO did it server-side —
// AFB-0078 found the browser supplying the actor eid, i.e. deciding for itself
// who signed off.
//
// Flat-fetch + client-side join, per the app convention. No PostgREST embeds.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { rpcErrorMessage } from '../../../shared/rpc-error';
import { fetchAmNameMap } from '../../../shared/am-name';
import type { MachineRow } from './use-fleet-data';

// ----- Row shapes -----------------------------------------------------------

export interface MovementEventRow {
  event_id: string;
  asset_type: string;
  asset_id: string;
  event_type: string;
  from_facility_id: string | null;
  from_office_id: string | null;
  to_facility_id: string | null;
  to_office_id: string | null;
  from_status: string | null;
  to_status: string | null;
  effective_at: string;
  recorded_at: string;
  actor_eid: string | null;
  source: string;
  note: string | null;
}

// location-picker.tsx was here and is GONE, not deprecated. It filtered this
// list in the browser, so it could only match words stored on the facility or
// office row: "HQ" found nothing, because "KC Headquarters" does not contain
// those letters in that order, and neither did Dock, MAV, Silverback or Holmes
// Road. Those words live in entity_aliases, which a browser filter cannot see.
// The live version is DestinationPicker in destination-picker.tsx, backed by
// rpc_asset_resolve_destination.

/** A place a machine can be: a facility or an office. The shape the RESOLVER
 *  returns — see useLocationNames below. */
export interface LocationOption {
  id: string;
  kind: 'facility' | 'office';
  name: string;
  subtitle: string | null;
  /** Facilities only — used to warn when moving to a closed site. */
  inactive: boolean;
}

/** OF-006 is "Remote / Overseas". A floor scrubber cannot be overseas, and
 *  rpc_asset_move rejects it outright with TCS_ASSET_REMOTE_NOT_A_PLACE. So no
 *  PICKER may offer it — a picker is a snapshot presented as a guarantee
 *  (TCSD-0041) and offering a destination the write refuses is the bug.
 *
 *  ⚠️ rpc_asset_resolve_destination DOES return OF-006 (searching "Remote"
 *  finds it, rank 2), so the picker built on that RPC has to filter it here.
 *  Closing that in the function is a migration; it is written up in the
 *  2026-08-27 run report as a proposal. */
export const EXCLUDED_OFFICE_IDS = ['OF-006'];

// ----- Naming a place -------------------------------------------------------

/**
 * ⛔ THIS IS A RESOLVER, NOT A PICKER (TCSD-0129).
 *
 * It returns EVERY facility and EVERY office — closed sites and OF-006
 * included — because its job is to put a NAME on an id that is already stored:
 * the movement-history timeline names places a machine has been, and a machine
 * really can have been left at a site TCS has since exited. Filtering here
 * would render those rows as a bare `FAC-0155`, which is the defect this run
 * exists to remove.
 *
 * Where a person CHOOSES a destination, use DestinationPicker
 * (destination-picker.tsx) — that is the picker, it is backed by
 * rpc_asset_resolve_destination, and it excludes what the write would refuse.
 * Binding a picker to a resolver is the bug, and it is invisible.
 */
export function useLocationNames() {
  const [all, setAll] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [fRes, oRes] = await Promise.all([
          supabase
            .from('facilities')
            .select('facility_id, facility_name, market, state, status')
            .order('facility_name'),
          supabase
            .from('office_locations')
            .select('office_id, office_name, office_location, office_region')
            .order('office_id'),
        ]);
        if (fRes.error) throw fRes.error;
        if (oRes.error) throw oRes.error;
        if (cancelled) return;

        const facilities: LocationOption[] = (fRes.data ?? []).map((f) => {
          const row = f as {
            facility_id: string;
            facility_name: string | null;
            market: string | null;
            state: string | null;
            status: string | null;
          };
          return {
            id: row.facility_id,
            kind: 'facility' as const,
            name: row.facility_name ?? row.facility_id,
            subtitle: [row.market ?? row.state, row.facility_id].filter(Boolean).join(' · ') || null,
            inactive: (row.status ?? '') === 'Closed',
          };
        });

        const offices: LocationOption[] = (oRes.data ?? [])
          .map((o) => {
            // NOTE: OF-006 is NOT filtered out here. See the hook's header —
            // this list is a resolver, and a resolver returns every row.
            const row = o as {
              office_id: string;
              office_name: string | null;
              office_location: string | null;
              office_region: string | null;
            };
            return {
              id: row.office_id,
              kind: 'office' as const,
              name: row.office_name ?? row.office_id,
              subtitle: row.office_location ?? row.office_region,
              inactive: false,
            };
          });

        setAll([...offices, ...facilities]);
      } catch (e) {
        if (!cancelled) setError(rpcErrorMessage(e, 'Could not load facilities and offices.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const byId = useMemo(() => new Map(all.map((o) => [o.id, o])), [all]);

  return { byId, loading, error };
}

// locationLabel() was here and is GONE, not deprecated. It resolved a machine's
// location by looking its id up in the map above, which meant every caller had
// to hold the map, and it returned the bare facility name where the standing
// rule is `Facility Name (Owner Name)`. The live version is machineLocation()
// in hooks/use-fleet-data.ts, which reads v_floor_machines.location_display —
// one string, derived once, in the database.

// ----- One machine, with its history ----------------------------------------

export function useEquipmentDetail(machineId: string | undefined) {
  const [machine, setMachine] = useState<MachineRow | null>(null);
  const [events, setEvents] = useState<MovementEventRow[]>([]);
  const [nameMap, setNameMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!machineId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const [mRes, eRes] = await Promise.all([
          // The VIEW, never the table — it carries location_display, which is
          // the only string this page may render for a place. See MachineRow.
          supabase.from('v_floor_machines').select('*').eq('machine_id', machineId).maybeSingle(),
          supabase
            .from('asset_movement_events')
            .select('*')
            .eq('asset_type', 'floor_machine')
            .eq('asset_id', machineId)
            .order('effective_at', { ascending: false })
            .limit(100),
        ]);
        if (mRes.error) throw mRes.error;
        if (eRes.error) throw eRes.error;
        if (cancelled) return;

        const m = (mRes.data ?? null) as MachineRow | null;
        // A machine that does not exist is a first-class outcome here, not an
        // error: 74 pre-printed tags will resolve to nothing for months.
        if (!m) {
          setNotFound(true);
          setMachine(null);
          setEvents([]);
          return;
        }
        const evs = (eRes.data ?? []) as MovementEventRow[];
        setMachine(m);
        setEvents(evs);

        const eids = Array.from(
          new Set(
            [
              m.assigned_employee_eid,
              m.last_confirmed_by_eid,
              ...evs.map((e) => e.actor_eid),
            ].filter((x): x is string => !!x),
          ),
        );
        if (eids.length) {
          const map = await fetchAmNameMap(eids);
          if (!cancelled) setNameMap(map);
        }
      } catch (e) {
        if (!cancelled) setError(rpcErrorMessage(e, 'Could not load this machine.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [machineId, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { machine, events, nameMap, loading, error, notFound, refetch };
}

// ----- Writes: RPC only -----------------------------------------------------

/** Where the write came from. Recorded on the event, because a qr_scan proves a
 *  code was VISIBLE, not that a person stood next to the machine. */
export type AssetWriteSource = 'qr_scan' | 'app' | 'chat' | 'voice';

/** "Yes, still here." Writes the confirmation columns and NO location. */
export async function confirmMachineLocation(
  machineId: string,
  source: AssetWriteSource = 'app',
): Promise<string> {
  const { data, error } = await supabase.rpc('rpc_asset_confirm_location', {
    p_asset_id: machineId,
    p_source: source,
  });
  if (error) throw error;
  return data as string;
}

/** Move to a facility or an office — exactly one. */
export async function moveMachine(args: {
  machineId: string;
  facilityId?: string | null;
  officeId?: string | null;
  effectiveAt?: string | null;
  note?: string | null;
  source?: AssetWriteSource;
  isCorrection?: boolean;
}): Promise<string> {
  const { data, error } = await supabase.rpc('rpc_asset_move', {
    p_asset_id: args.machineId,
    p_facility_id: args.facilityId ?? null,
    p_office_id: args.officeId ?? null,
    p_effective_at: args.effectiveAt ?? null,
    p_note: args.note ?? null,
    p_source: args.source ?? 'app',
    p_is_correction: args.isCorrection ?? false,
  });
  if (error) throw error;
  return data as string;
}

/**
 * Move by NAME — the free-text path. The database resolves the words, and when
 * it cannot resolve them to exactly one place it REFUSES rather than guessing:
 * DESTINATION_NOT_FOUND, or DESTINATION_AMBIGUOUS carrying the candidates.
 * See destination-errors.ts for turning those back into a screen.
 *
 * Eligibility is unchanged — this delegates to rpc_asset_move, which calls
 * assert_may_update_asset() first. The wrapper widens nothing.
 */
export async function moveMachineByName(args: {
  machineId: string;
  destination: string;
  effectiveAt?: string | null;
  note?: string | null;
  source?: AssetWriteSource;
  isCorrection?: boolean;
}): Promise<string> {
  const { data, error } = await supabase.rpc('rpc_asset_move_by_name', {
    p_asset_id: args.machineId,
    p_destination: args.destination,
    p_effective_at: args.effectiveAt ?? null,
    p_note: args.note ?? null,
    p_source: args.source ?? 'app',
    p_is_correction: args.isCorrection ?? false,
  });
  if (error) throw error;
  return data as string;
}

/** Change status. needs_repair / out_for_repair also DMs the national AM. */
export async function setMachineStatus(args: {
  machineId: string;
  status: string;
  note?: string | null;
  source?: AssetWriteSource;
}): Promise<string> {
  const { data, error } = await supabase.rpc('rpc_asset_set_status', {
    p_asset_id: args.machineId,
    p_status: args.status,
    p_note: args.note ?? null,
    p_source: args.source ?? 'app',
  });
  if (error) throw error;
  return data as string;
}

/**
 * Force a specific number onto a machine — Ben's backstop when the printed tag
 * has to win (TCSD-0143).
 *
 * Minting fills the lowest unused number, so a stray number heals itself as
 * soon as another machine is added. This is the other case: the tag is already
 * stuck to something and the DATABASE is the thing that has to move.
 *
 * ⛔ Narrower than saveMachineSpec on purpose — the RPC allows superuser and
 * executive only, because this is a physical-inventory correction rather than
 * day-to-day editing. canRenumberMachine() (shared/role-gates) must stay in
 * step with it; the database is the authority and that helper only decides
 * whether the button is drawn.
 *
 * The reason is mandatory and is written onto the machine's own history.
 */

export async function renumberMachine(args: {
  machineId: string;
  newMachineId: string;
  reason: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc('rpc_asset_renumber_machine', {
    p_machine_id: args.machineId,
    p_new_machine_id: args.newMachineId,
    p_reason: args.reason,
  });
  if (error) throw error;
  return data as string;
}

/** Spec fields only — make, model, serial, assignee, dates, notes.
 *  Cannot write location or status by design; those have their own RPCs. */
export async function saveMachineSpec(args: {
  machineId: string | null;
  brand: string | null;
  model: string | null;
  serial?: string | null;
  assignedEid?: string | null;
  lastMaintenance?: string | null;
  nextMaintenance?: string | null;
  purchaseDate?: string | null;
  warrantyExp?: string | null;
  notes?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('rpc_asset_save_machine', {
    p_machine_id: args.machineId,
    p_brand: args.brand,
    p_model: args.model,
    p_serial: args.serial ?? null,
    p_assigned_eid: args.assignedEid ?? null,
    p_last_maintenance: args.lastMaintenance ?? null,
    p_next_maintenance: args.nextMaintenance ?? null,
    p_purchase_date: args.purchaseDate ?? null,
    p_warranty_exp: args.warrantyExp ?? null,
    p_notes: args.notes ?? null,
  });
  if (error) throw error;
  return data as string;
}
